import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { analyticsAPI, invoicesAPI } from '../services/api'
import { User, TrendingUp, TrendingDown, Download, Calendar, Briefcase, FileText, Bell, HelpCircle, Info, ExternalLink, ArrowLeft } from 'lucide-react'

const EarningsPage = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('overview')
  const [selectedPeriod, setSelectedPeriod] = useState('current-month')
  const [invoiceFilter, setInvoiceFilter] = useState('all') // 'all', 'sent', 'received'
  const [invoices, setInvoices] = useState({ sent: [], received: [] })
  const [invoicesLoading, setInvoicesLoading] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [earnings, setEarnings] = useState({
    overview: {
      totalEarnings: 0,
      pendingPayments: 0,
      thisMonth: 0,
      lastMonth: 0,
      averagePerProject: 0,
      completedProjects: 0
    },
    transactions: [],
    monthlyBreakdown: []
  })


  // Fetch earnings data
  const fetchEarnings = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await analyticsAPI.getEarnings()

      if (response.data) {
        setEarnings(response.data)
      }
    } catch (error) {
      console.error('Error fetching earnings:', error)
      setError('Er is een fout opgetreden bij het laden van earnings data')
    } finally {
      setLoading(false)
    }
  }

  const fetchInvoices = async () => {
    try {
      setInvoicesLoading(true)
      const response = await invoicesAPI.getMyInvoices()
      if (response.data?.success) {
        setInvoices(response.data.data)
      }
    } catch (err) {
      console.error('Error fetching invoices:', err)
    } finally {
      setInvoicesLoading(false)
    }
  }

  useEffect(() => {
    if (!user || user.role !== 'creator') {
      navigate('/dashboard')
      return
    }
    fetchEarnings()
  }, [user, navigate])

  useEffect(() => {
    if (activeTab === 'invoices' && invoices.sent.length === 0 && invoices.received.length === 0) {
      fetchInvoices()
    }
  }, [activeTab])

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2
    }).format(amount)
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'text-emerald-600 bg-emerald-50'
      case 'pending':
        return 'text-yellow-600 bg-yellow-50'
      case 'processing':
        return 'text-blue-400 bg-emerald-50'
      case 'cancelled':
        return 'text-red-500 bg-red-50'
      default:
        return 'text-gray-400 bg-gray-100'
    }
  }

  const getStatusText = (status) => {
    switch (status) {
      case 'paid':
        return 'Betaald'
      case 'pending':
        return 'In afwachting'
      case 'processing':
        return 'Wordt verwerkt'
      case 'cancelled':
        return 'Geannuleerd'
      case 'netted':
        return 'Verrekend'
      case 'refunded':
        return 'Terugbetaald'
      default:
        return status
    }
  }

  const getInvoiceTypeText = (type) => {
    switch (type) {
      case 'sales_deposit':
        return 'Verkoopfactuur (aanbetaling)'
      case 'sales_final':
        return 'Verkoopfactuur (restbetaling)'
      case 'purchase_self_billing':
        return 'Inkoopfactuur (self-billing)'
      case 'platform_fee':
        return 'Platformfee'
      default:
        return 'Factuur'
    }
  }

  const handleDownloadPdf = async (invoiceId, invoiceNumber) => {
    try {
      const response = await invoicesAPI.downloadPdf(invoiceId)
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `${invoiceNumber}.pdf`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Error downloading PDF:', err)
    }
  }

  const getFilteredInvoices = () => {
    if (invoiceFilter === 'sent') return invoices.sent
    if (invoiceFilter === 'received') return invoices.received
    return [...invoices.sent, ...invoices.received].sort(
      (a, b) => new Date(b.invoiceDate) - new Date(a.invoiceDate)
    )
  }

  // Create dynamic icon components
  const DynamicIcon = ({ iconName, className }) => {
    const iconMap = {
      'euro': () => <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h1m4 0h1m-6-8h8a2 2 0 012 2v8a2 2 0 01-2 2H8a2 2 0 01-2-2V6a2 2 0 012-2z"/></svg>,
      'clock': () => <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>,
      'credit-card': () => <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/></svg>,
      'briefcase': () => <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0H8m8 0v10a2 2 0 01-2 2H10a2 2 0 01-2-2V6m8 0H8"/></svg>
    }
    const IconComponent = iconMap[iconName]
    return IconComponent ? <IconComponent /> : null
  }

  const StatCard = ({ title, amount, subtitle, icon, change, color = 'sky' }) => (
    <div className="group relative">
      <div className="relative p-4 md:p-6 rounded-2xl bg-white shadow-md border border-gray-200 hover:border-gray-300 transition-all">
        <div className="flex items-center justify-between mb-3 md:mb-4">
          <div className={`w-10 h-10 md:w-12 md:h-12 bg-${color === 'sky' ? 'blue' : color === 'green' ? 'emerald' : color === 'yellow' ? 'yellow' : 'purple'}-50 rounded-xl flex items-center justify-center`}>
            <DynamicIcon iconName={icon} className={`w-6 h-6 text-${color === 'sky' ? '[#1D8BF8]' : color === 'green' ? 'emerald-600' : color === 'yellow' ? 'yellow-600' : '[#7c3aed]'}`} />
          </div>
          {change && (
            <div className={`text-xs font-medium ${change > 0 ? 'text-emerald-600' : 'text-red-500'} flex items-center`}>
              {change > 0 ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
              {change > 0 ? '+' : ''}{change}%
            </div>
          )}
        </div>
        <div className="text-xl md:text-2xl font-bold text-[#0F172A] mb-1 break-words">{formatCurrency(amount)}</div>
        <div className="text-sm text-[#475569] mb-2">{title}</div>
        {subtitle && <div className="text-xs text-[#475569]/50 break-words">{subtitle}</div>}
      </div>
    </div>
  )

  if (!user || user.role !== 'creator') {
    return null
  }

  return (
    <div className="font-['Inter'] min-h-screen bg-white text-[#0F172A] overflow-x-hidden">

      {/* Header */}
      <header className="w-full flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gray-100 sticky top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-xl">
        <Link to="/" className="text-2xl font-black tracking-tighter text-[#0F172A]">
          UGC<span className="text-[#3EB489]">4you</span>
        </Link>

        {/* Mobiel: de nav hieronder is verborgen, dus een expliciete weg terug naar het dashboard */}
        <Link to="/dashboard" className="md:hidden inline-flex items-center gap-2 min-h-[44px] px-3 -mr-2 rounded-lg text-sm font-medium hover:bg-gray-100 transition">
          <ArrowLeft className="w-5 h-5" />
          Dashboard
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          <Link to="/projects" className="hover:text-[#3EB489] transition font-medium">Opdrachten</Link>
          <Link to="/creators" className="hover:text-[#3EB489] transition font-medium">Creators</Link>
          <Link to="/dashboard" className="hover:text-[#3EB489] transition font-medium">Dashboard</Link>

          <div className="flex items-center gap-4">
            <div className="text-right hidden md:block">
              <div className="text-sm font-semibold">{user.firstName}</div>
              <div className="text-xs text-[#475569]">Creator</div>
            </div>
            <div className="w-10 h-10 bg-[#3EB489] rounded-full flex items-center justify-center">
              {user.profilePictureUrl ? (
                <img src={user.profilePictureUrl} alt="Profile" className="w-10 h-10 rounded-full object-cover" />
              ) : (
                <User className="w-5 h-5 text-white" />
              )}
            </div>
          </div>
        </nav>
      </header>

      <div className="relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          {/* Header Section */}
          <div className="mb-8">
            <h1 className="text-2xl md:text-4xl font-black mb-2">Inkomsten</h1>
            <p className="text-[#475569] text-base md:text-lg">
              Overzicht van je verdiensten en betalingen
            </p>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#1D8BF8]"></div>
              <p className="mt-4 text-[#475569]">Earnings data worden geladen...</p>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-2xl p-6 mb-8">
              <p className="text-red-500">{error}</p>
              <button
                onClick={fetchEarnings}
                className="mt-4 px-4 py-2 bg-red-50 hover:bg-red-100 text-red-500 rounded-xl transition"
              >
                Opnieuw proberen
              </button>
            </div>
          )}

          {/* Stats Overview */}
          {!loading && !error && (
            <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6 mb-8">
            <StatCard
              title="Totaal Verdiend"
              amount={earnings.overview.totalEarnings}
              subtitle="Alle tijd"
              icon="euro"
              change={12}
              color="green"
            />
            <StatCard
              title="Deze Maand"
              amount={earnings.overview.thisMonth}
              subtitle={`vs. €${earnings.overview.lastMonth} vorige maand`}
              icon="calendar"
              change={19}
              color="sky"
            />
            <StatCard
              title="In Afwachting"
              amount={earnings.overview.pendingPayments}
              subtitle="Wordt binnenkort uitbetaald"
              icon="clock"
              color="yellow"
            />
            <StatCard
              title="Gemiddeld per Project"
              amount={earnings.overview.averagePerProject}
              subtitle={`${earnings.overview.completedProjects} voltooide projecten`}
              icon="trending-up"
              change={8}
              color="purple"
            />
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap gap-2 md:gap-1 mb-6 md:mb-8">
            {[
              { id: 'overview', label: 'Overzicht' },
              { id: 'transactions', label: 'Transacties' },
              { id: 'invoices', label: 'Facturen' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 md:px-6 py-2.5 md:py-3 rounded-xl md:rounded-2xl text-sm md:text-base font-medium whitespace-nowrap transition ${
                  activeTab === tab.id
                    ? 'bg-[#3EB489] text-white'
                    : 'bg-[#F8FAFC] text-[#475569] hover:bg-gray-100 hover:text-[#0F172A]'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="relative">
            <div className="relative p-4 md:p-8 rounded-2xl md:rounded-3xl bg-white shadow-md border border-gray-200">

              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div className="space-y-6 md:space-y-8">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <h3 className="text-xl font-bold">Maandelijks Overzicht</h3>
                    <button className="text-sm text-[#3EB489] hover:text-[#1670c9] transition inline-flex items-center">
                      Export Data
                      <Download className="w-4 h-4 ml-2" />
                    </button>
                  </div>

                  <div className="grid gap-4">
                    {earnings.monthlyBreakdown.map((month, index) => (
                      <div key={index} className="p-4 md:p-6 bg-[#F8FAFC] rounded-2xl hover:bg-gray-100 transition">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3">
                          <div className="flex items-center min-w-0">
                            <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                              <Calendar className="w-6 h-6 text-[#3EB489]" />
                            </div>
                            <div className="min-w-0">
                              <div className="text-lg font-semibold text-[#0F172A]">{month.month}</div>
                              <div className="text-sm text-[#475569]">{month.projects} projecten</div>
                            </div>
                          </div>

                          {/* Op mobiel onder de maandnaam (uitgelijnd met de tekst), vanaf sm rechts */}
                          <div className="text-left sm:text-right pl-16 sm:pl-0 sm:flex-shrink-0">
                            <div className="text-xl md:text-2xl font-bold text-[#0F172A]">{formatCurrency(month.earnings)}</div>
                            <div className="text-sm text-[#475569]">
                              {formatCurrency(month.earnings / month.projects)} gem. per project
                            </div>
                          </div>
                        </div>

                        {/* Progress bar showing relative earnings */}
                        <div className="mt-4">
                          <div className="h-2 bg-[#F8FAFC] rounded-full overflow-hidden">
                            <div
                              className="h-full bg-[#3EB489] rounded-full transition-all duration-1000"
                              style={{
                                width: `${(month.earnings / Math.max(...earnings.monthlyBreakdown.map(m => m.earnings))) * 100}%`
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Transactions Tab */}
              {activeTab === 'transactions' && (
                <div className="space-y-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <h3 className="text-xl font-bold">Recente Transacties</h3>
                    <div className="flex gap-3 w-full sm:w-auto">
                      <select
                        value={selectedPeriod}
                        onChange={(e) => setSelectedPeriod(e.target.value)}
                        className="w-full sm:w-auto bg-[#F8FAFC] border border-gray-200 rounded-xl px-4 py-2 text-[#0F172A] text-base sm:text-sm"
                      >
                        <option value="current-month">Deze maand</option>
                        <option value="last-month">Vorige maand</option>
                        <option value="last-3-months">Laatste 3 maanden</option>
                        <option value="all-time">Alle tijd</option>
                      </select>
                    </div>
                  </div>

                  {/* Mobiel: kaart per transactie (een geplette 5-kolomstabel is onleesbaar op 360 px) */}
                  <div className="md:hidden divide-y divide-gray-100 border-t border-gray-200">
                    {earnings.transactions.map((transaction) => (
                      <div key={transaction.id} className="py-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-[#0F172A] break-words">{transaction.projectTitle}</div>
                            {transaction.company && <div className="text-xs text-[#475569] mt-0.5 break-words">{transaction.company}</div>}
                            <div className="text-xs text-[#475569]/50 mt-0.5">#{transaction.invoice}</div>
                          </div>
                          <div className="text-sm font-semibold text-[#0F172A] whitespace-nowrap">{formatCurrency(transaction.amount)}</div>
                        </div>
                        <div className="flex items-center justify-between gap-3 mt-3">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(transaction.status)}`}>
                            {getStatusText(transaction.status)}
                          </span>
                          <span className="text-xs text-[#475569]">
                            {transaction.date ? new Date(transaction.date).toLocaleDateString('nl-NL') : 'N/A'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full min-w-[560px]">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-4 text-sm font-medium text-[#475569]">Project</th>
                          <th className="text-left py-4 text-sm font-medium text-[#475569]">Bedrijf</th>
                          <th className="text-right py-4 text-sm font-medium text-[#475569]">Bedrag</th>
                          <th className="text-center py-4 text-sm font-medium text-[#475569]">Status</th>
                          <th className="text-right py-4 text-sm font-medium text-[#475569]">Datum</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {earnings.transactions.map((transaction) => (
                          <tr key={transaction.id} className="hover:bg-[#F8FAFC] transition">
                            <td className="py-4">
                              <div className="flex items-center">
                                <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center mr-3">
                                  <Briefcase className="w-4 h-4 text-[#3EB489]" />
                                </div>
                                <div>
                                  <div className="text-sm font-medium text-[#0F172A]">{transaction.projectTitle}</div>
                                  <div className="text-xs text-[#475569]/50">#{transaction.invoice}</div>
                                </div>
                              </div>
                            </td>
                            <td className="py-4 text-sm text-[#0F172A]">{transaction.company}</td>
                            <td className="py-4 text-right text-sm font-semibold text-[#0F172A]">
                              {formatCurrency(transaction.amount)}
                            </td>
                            <td className="py-4 text-center">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(transaction.status)}`}>
                                {getStatusText(transaction.status)}
                              </span>
                            </td>
                            <td className="py-4 text-right text-sm text-[#475569]">
                              {transaction.date ? new Date(transaction.date).toLocaleDateString('nl-NL') : 'N/A'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Invoices Tab */}
              {activeTab === 'invoices' && (
                <div className="space-y-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <h3 className="text-xl font-bold">Facturen</h3>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { id: 'all', label: 'Alle' },
                        { id: 'sent', label: 'Verzonden' },
                        { id: 'received', label: 'Ontvangen' }
                      ].map((filter) => (
                        <button
                          key={filter.id}
                          onClick={() => setInvoiceFilter(filter.id)}
                          className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition ${
                            invoiceFilter === filter.id
                              ? 'bg-[#3EB489] text-white'
                              : 'bg-[#F8FAFC] text-[#475569] hover:bg-gray-100'
                          }`}
                        >
                          {filter.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {invoicesLoading ? (
                    <div className="text-center py-12">
                      <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-[#1D8BF8]"></div>
                      <p className="mt-3 text-sm text-[#475569]">Facturen laden...</p>
                    </div>
                  ) : getFilteredInvoices().length === 0 ? (
                    <div className="text-center py-16">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-50 flex items-center justify-center">
                        <FileText className="w-8 h-8 text-[#3EB489]" />
                      </div>
                      <h4 className="text-lg font-semibold mb-1">Geen facturen</h4>
                      <p className="text-[#475569] text-sm">Er zijn nog geen facturen beschikbaar.</p>
                    </div>
                  ) : (
                    <>
                    {/* Mobiel: kaart per factuur, met een volwaardige downloadknop (was een kaal 16 px icoon) */}
                    <div className="md:hidden divide-y divide-gray-100 border-t border-gray-200">
                      {getFilteredInvoices().map((invoice) => (
                        <div key={invoice.id} className="py-4">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-start gap-3 min-w-0">
                              <div className="w-8 h-8 bg-emerald-50 rounded-lg flex items-center justify-center flex-shrink-0">
                                <FileText className="w-4 h-4 text-[#3EB489]" />
                              </div>
                              <div className="min-w-0">
                                <div className="text-sm font-medium text-[#0F172A] break-all">{invoice.invoiceNumber}</div>
                                <div className="text-xs text-[#475569] mt-0.5">{getInvoiceTypeText(invoice.type)}</div>
                              </div>
                            </div>
                            <div className="text-sm font-semibold text-[#0F172A] whitespace-nowrap">{formatCurrency(invoice.total)}</div>
                          </div>
                          <div className="flex items-center justify-between gap-3 mt-3">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(invoice.status)}`}>
                                {getStatusText(invoice.status)}
                              </span>
                              <span className="text-xs text-[#475569]">
                                {invoice.invoiceDate ? new Date(invoice.invoiceDate).toLocaleDateString('nl-NL') : 'N/A'}
                              </span>
                            </div>
                            <button
                              onClick={() => handleDownloadPdf(invoice.id, invoice.invoiceNumber)}
                              className="inline-flex items-center gap-1.5 min-h-[44px] px-3 rounded-lg border border-[#3EB489] text-[#3EB489] text-sm font-medium hover:bg-emerald-50 transition flex-shrink-0"
                              title="Download PDF"
                            >
                              <Download className="w-4 h-4" />
                              PDF
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="hidden md:block overflow-x-auto">
                      <table className="w-full min-w-[640px]">
                        <thead>
                          <tr className="border-b border-gray-200">
                            <th className="text-left py-4 text-sm font-medium text-[#475569]">Factuurnummer</th>
                            <th className="text-left py-4 text-sm font-medium text-[#475569]">Type</th>
                            <th className="text-right py-4 text-sm font-medium text-[#475569]">Bedrag</th>
                            <th className="text-center py-4 text-sm font-medium text-[#475569]">Status</th>
                            <th className="text-right py-4 text-sm font-medium text-[#475569]">Datum</th>
                            <th className="text-center py-4 text-sm font-medium text-[#475569]">PDF</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {getFilteredInvoices().map((invoice) => (
                            <tr key={invoice.id} className="hover:bg-[#F8FAFC] transition">
                              <td className="py-4">
                                <div className="flex items-center">
                                  <div className="w-8 h-8 bg-emerald-50 rounded-lg flex items-center justify-center mr-3">
                                    <FileText className="w-4 h-4 text-[#3EB489]" />
                                  </div>
                                  <span className="text-sm font-medium text-[#0F172A]">{invoice.invoiceNumber}</span>
                                </div>
                              </td>
                              <td className="py-4 text-sm text-[#475569]">
                                {getInvoiceTypeText(invoice.type)}
                              </td>
                              <td className="py-4 text-right text-sm font-semibold text-[#0F172A]">
                                {formatCurrency(invoice.total)}
                              </td>
                              <td className="py-4 text-center">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(invoice.status)}`}>
                                  {getStatusText(invoice.status)}
                                </span>
                              </td>
                              <td className="py-4 text-right text-sm text-[#475569]">
                                {invoice.invoiceDate ? new Date(invoice.invoiceDate).toLocaleDateString('nl-NL') : 'N/A'}
                              </td>
                              <td className="py-4 text-center">
                                <button
                                  onClick={() => handleDownloadPdf(invoice.id, invoice.invoiceNumber)}
                                  className="inline-flex items-center justify-center p-2.5 -m-2.5 rounded-lg text-[#3EB489] hover:text-[#1670c9] transition"
                                  title="Download PDF"
                                >
                                  <Download className="w-4 h-4" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Payment Info Card */}
          <div className="relative mt-8">
            <div className="relative p-4 md:p-6 rounded-2xl bg-white shadow-md border border-gray-200">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div className="flex items-start min-w-0">
                  <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                    <Info className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div className="min-w-0">
                    <h4 className="font-semibold text-[#0F172A] mb-1">Betalingsinformatie</h4>
                    <p className="text-sm text-[#475569]">
                      Uitbetalingen worden elke vrijdag verwerkt. Zorg dat je bankgegevens up-to-date zijn.
                    </p>
                  </div>
                </div>
                <Link
                  to="/profile"
                  className="text-sm text-[#3EB489] hover:text-[#1670c9] transition inline-flex items-center min-h-[44px] sm:min-h-0 pl-16 sm:pl-0 flex-shrink-0"
                >
                  Bijwerken
                  <ExternalLink className="w-4 h-4 ml-1" />
                </Link>
              </div>
            </div>
          </div>
          </>
          )}
        </div>
      </div>
    </div>
  )
}

export default EarningsPage